import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@lf/ui-primitives';
import { submitCoppaAgeCheck } from '../api/lfApiChildLink';
import CoppaConsentPendingCard from '../cards/student/CoppaConsentPendingCard';
import { useUser } from '../contexts/UserContext';

function ChildAgeCheckPage() {
    const { fetchUser } = useUser();
    const [step, setStep] = useState('ask'); // 'ask' | 'parent_email' | 'pending'
    const [parentEmail, setParentEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const emailRegex = /^[^@]+@[^@]+\.[^@]+$/;
    const isParentEmailValid = emailRegex.test(parentEmail.trim());

    async function handleAnswerNo() {
        setError('');
        setBusy(true);
        const resp = await submitCoppaAgeCheck(false);
        setBusy(false);
        if (resp && resp.data && resp.data.success) {
            if (fetchUser) await fetchUser();
            navigate('/');
        } else {
            setError('Could not save your answer. Please try again.');
        }
    }

    function handleAnswerYes() {
        setStep('parent_email');
    }

    async function handleSubmitParentEmail(e) {
        if (e) e.preventDefault();
        setError('');
        if (!isParentEmailValid) {
            setError('Please enter a valid email address.');
            return;
        }
        setBusy(true);
        const resp = await submitCoppaAgeCheck(true, parentEmail.trim());
        setBusy(false);
        if (resp && resp.data && resp.data.success) {
            setStep('pending');
        } else {
            setError('Could not send the approval email. Please try again.');
        }
    }

    if (step === 'pending') {
        return (
            <CoppaConsentPendingCard
                initialEmail={parentEmail}
                fetchUser={fetchUser}
            />
        );
    }

    if (step === 'parent_email') {
        return (
            <div className="mx-auto max-w-screen-md my-8">
                <div className="border border-gray-300 bg-white rounded-2xl py-6 px-6 sm:px-12 text-left">
                    <h2 className="text-xl font-semibold mb-3">
                        We need a grown-up to say it&apos;s OK
                    </h2>
                    <p className="text-sm text-gray-700 mb-3">
                        Because you said you are under 13, a parent or guardian
                        needs to give us permission before you can use Learnforest.
                        Type their email below and we&apos;ll send them a link to
                        approve your account.
                    </p>
                    <form onSubmit={handleSubmitParentEmail} className="flex flex-col gap-2">
                        <input
                            type="email"
                            className="border border-gray-300 p-2 w-full"
                            placeholder="Parent's email address"
                            value={parentEmail}
                            onChange={e => setParentEmail(e.target.value)}
                        />
                        {error && <div className="text-red-500 text-sm">{error}</div>}
                        <LoadingButton
                            type="submit"
                            className="bg-blue-500 text-white py-2 px-4 rounded self-start"
                            text="Send approval email"
                            isLoading={busy}
                            disabled={!isParentEmailValid}
                            onClick={handleSubmitParentEmail}
                        />
                    </form>
                </div>
            </div>
        );
    }

    // step === 'ask'
    return (
        <div className="mx-auto max-w-screen-md my-8">
            <div className="border border-gray-300 bg-white rounded-2xl py-6 px-6 sm:px-12 text-left">
                <h2 className="text-xl font-semibold mb-3">
                    One quick question
                </h2>
                <p className="text-sm text-gray-700 mb-4">
                    Are you under 13 years old?
                </p>
                {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
                <div className="flex gap-2">
                    <LoadingButton
                        className="bg-blue-500 text-white py-2 px-6 rounded"
                        text="Yes"
                        isLoading={busy}
                        onClick={handleAnswerYes}
                    />
                    <LoadingButton
                        className="bg-blue-500 text-white py-2 px-6 rounded"
                        text="No"
                        isLoading={busy}
                        onClick={handleAnswerNo}
                    />
                </div>
            </div>
        </div>
    );
}

export default ChildAgeCheckPage;
